/**
 * DashboardCard — single tile in the post-login launcher grid.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { DashboardEntry, DashboardTone } from '../../config/dashboards';

interface DashboardCardProps {
  entry: DashboardEntry;
}

const toneMap: Record<DashboardTone, { bg: string; soft: string; fg: string }> = {
  accent: { bg: 'var(--accent)',         soft: 'var(--accent-soft)',   fg: 'var(--accent-soft-fg)' },
  spark:  { bg: 'var(--accent-2)',       soft: 'var(--accent-2-soft)', fg: 'var(--accent-2-soft-fg)' },
  plum:   { bg: 'var(--cg-plum-600)',    soft: 'var(--cg-plum-50)',    fg: 'var(--cg-plum-600)' },
  forest: { bg: 'var(--cg-forest-600)',  soft: 'var(--cg-forest-50)',  fg: 'var(--cg-forest-600)' },
  teal:   { bg: 'var(--cg-teal-600)',    soft: 'var(--cg-teal-50)',    fg: 'var(--cg-teal-600)' },
};

const DashboardCard: React.FC<DashboardCardProps> = ({ entry }) => {
  const navigate = useNavigate();
  const tone = toneMap[entry.tone ?? 'accent'];

  return (
    <button
      type="button"
      onClick={() => navigate(entry.route)}
      className="cg-card--interactive"
      style={styles.card}
      aria-label={`Open ${entry.label} dashboard`}
    >
      <div
        style={{ ...styles.iconWrap, background: tone.soft, color: tone.fg }}
        aria-hidden="true"
      >
        <span style={{ fontSize: 22, lineHeight: 1 }}>{entry.icon}</span>
      </div>
      <div style={styles.title}>{entry.label}</div>
      <div style={styles.description}>{entry.description}</div>
      <div style={styles.footer}>
        <div style={styles.ctaLabel}>Open</div>
        <div
          style={{ ...styles.openArrow, background: tone.bg }}
          aria-hidden="true"
        >
          →
        </div>
      </div>
    </button>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 20,
    minHeight: 200,
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-xs)',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    color: 'var(--fg-1)',
    textAlign: 'left',
    width: '100%',
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 'var(--radius-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--fg-1)',
    letterSpacing: '-0.01em',
    fontFamily: 'var(--font-display)',
  },
  description: {
    fontSize: 13,
    color: 'var(--fg-3)',
    lineHeight: 1.55,
    flex: 1,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 14,
    borderTop: '1px solid var(--border-subtle)',
  },
  ctaLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
  },
  openArrow: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
  },
};

export default DashboardCard;
